import type {
  Asset,
  Assumptions,
  ClientCreate,
  ClientPlan,
  ClientListItem,
  TeamMember,
  ClientRead,
  ExampleClientOpen,
  ExampleClientOpenResponse,
  Firm,
  AdviserProfile,
  IncomeStream,
  IsoDate,
  Liability,
  LifeEvent,
  ReportExport,
  Scenario,
  SpendingItem,
  WealthProjectionRequest,
  WealthProjectionResult,
} from '@cf/shared';
import {
  adviserProfileSchema,
  clientListSchema,
  clientReadSchema,
  exampleClientOpenResponseSchema,
  firmSchema,
  teamOverviewResponseSchema,
} from '@cf/shared';
import { API_BASE_URL } from '../apiBase';
import { ExampleUnavailableError } from '../planDataClient';
import type { ClientListQuery, IPlanDataClient, PlanItemKind } from '../planDataClient';
import { sessionExpiryError } from '../sessionExpiry';
import { getTaxRulesBasis } from '../taxRulesBasis';

/**
 * Everything the discrete-plan / scenario / report surface needs is still
 * fixture-only (the older client-scoped model has no live API yet). Those
 * pages are hidden from the live-client flow — the builder is the client home —
 * so these methods should never be reached over the API. If one is, fail LOUD
 * with a clear message rather than silently returning fake data.
 */
function notLiveYet(what: string): never {
  throw new Error(`${what} is not available for live clients yet.`);
}

/**
 * Real /api/v1 implementation of IPlanDataClient. Mirrors ApiHouseholdClient:
 * every call carries the auth session's bearer token so reads/writes are
 * RLS-scoped, and responses are re-validated through the shared contracts.
 *
 * SECURITY (hard-rules #1–4): client data only ever crosses this seam through
 * the RLS-protected API — never the browser. See PlanDataProvider for the
 * configured-vs-fixture switch.
 */
export class ApiPlanDataClient implements IPlanDataClient {
  constructor(
    private readonly getToken: () => string | null,
    private readonly fixedAsOfDate?: IsoDate,
    private readonly fixedStartYear?: number,
  ) {}

  // No fixed override (the normal case) → the session's selected tax-rules
  // basis, read at CALL time so a basis switch reaches every later request.
  getAsOfDate(): IsoDate {
    return this.fixedAsOfDate ?? getTaxRulesBasis().asOfDate;
  }

  getStartYear(): number {
    return this.fixedStartYear ?? getTaxRulesBasis().startYear;
  }

  /** `content-type` is declared ONLY when a body is actually sent: a bodyless
   *  request that claims `application/json` is rejected by the API's JSON
   *  parser before the route is reached (an empty body is not valid JSON). */
  private headers(hasBody = true): Record<string, string> {
    const token = this.getToken();
    return {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(token !== null ? { authorization: `Bearer ${token}` } : {}),
    };
  }

  private async request(path: string, init?: RequestInit): Promise<unknown> {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: this.headers(init?.body !== undefined),
    });
    if (!res.ok) {
      throw sessionExpiryError(res) ?? new Error(`Request to ${path} failed (HTTP ${res.status})`);
    }
    const body = (await res.json()) as { data: unknown };
    return body.data;
  }

  async getFirm(): Promise<Firm> {
    return firmSchema.parse(await this.request('/api/v1/firm'));
  }

  async getAdviser(): Promise<AdviserProfile> {
    return adviserProfileSchema.parse(await this.request('/api/v1/adviser'));
  }

  async listClients(query: ClientListQuery): Promise<ClientListItem[]> {
    // The API returns every non-deleted client for the firm; status/search
    // narrowing matches the fixture semantics and stays client-side.
    //
    // Paging is deliberately NOT split from this (aspect 3, 2026-08-26): move
    // paging to the server while search stays here and the search box silently
    // only searches page one. At a 1–10 adviser firm's book — a few hundred
    // names and dates, no monetary values — the single fetch is the honest
    // shape. If a real firm's book ever makes this hurt, both move together.
    const all = clientListSchema.parse(await this.request('/api/v1/clients'));
    const status = query.status ?? 'active';
    const search = (query.search ?? '').trim().toLowerCase();
    return all.filter((c) => {
      if (status !== 'all' && c.status !== status) return false;
      if (search.length > 0 && !c.displayName.toLowerCase().includes(search)) return false;
      return true;
    });
  }

  async getClient(clientId: string): Promise<ClientRead> {
    return clientReadSchema.parse(
      await this.request(`/api/v1/clients/${encodeURIComponent(clientId)}`),
    );
  }

  async createClient(input: ClientCreate): Promise<ClientRead> {
    return clientReadSchema.parse(
      await this.request('/api/v1/clients', { method: 'POST', body: JSON.stringify(input) }),
    );
  }

  openExampleClient(clock: ExampleClientOpen): Promise<ExampleClientOpenResponse> {
    return this.exampleRequest('/api/v1/clients/example', clock);
  }

  resetExampleClient(clock: ExampleClientOpen): Promise<ExampleClientOpenResponse> {
    return this.exampleRequest('/api/v1/clients/example/reset', clock);
  }

  /** A 503 is its own error: "not available yet" is a state the screen explains, not a failure. */
  private async exampleRequest(
    path: string,
    clock: ExampleClientOpen,
  ): Promise<ExampleClientOpenResponse> {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: this.headers(true),
      body: JSON.stringify(clock),
    });
    if (res.status === 503) {
      const retryAfter = Number(res.headers?.get?.('retry-after'));
      throw new ExampleUnavailableError(
        'The example isn’t available right now.',
        Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter, 600) : 60,
      );
    }
    if (!res.ok) {
      throw sessionExpiryError(res) ?? new Error(`Request to ${path} failed (HTTP ${res.status})`);
    }
    const body = (await res.json()) as { data: unknown };
    return exampleClientOpenResponseSchema.parse(body.data);
  }

  private async setStatus(clientId: string, status: 'active' | 'archived'): Promise<ClientRead> {
    return clientReadSchema.parse(
      await this.request(`/api/v1/clients/${encodeURIComponent(clientId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    );
  }

  archiveClient(clientId: string): Promise<ClientRead> {
    return this.setStatus(clientId, 'archived');
  }

  restoreClient(clientId: string): Promise<ClientRead> {
    return this.setStatus(clientId, 'active');
  }

  async listTeamMembers(): Promise<TeamMember[]> {
    return teamOverviewResponseSchema.parse(await this.request('/api/v1/team')).members;
  }

  async reassignClient(clientId: string, adviserId: string): Promise<void> {
    await this.request(`/api/v1/clients/${encodeURIComponent(clientId)}/adviser`, {
      method: 'POST',
      body: JSON.stringify({ adviserId }),
    });
  }

  /** Soft delete. The route answers 204 with no body, so this cannot go
   *  through `request()` (which parses a JSON envelope). */
  async deleteClient(clientId: string): Promise<void> {
    const path = `/api/v1/clients/${encodeURIComponent(clientId)}`;
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'DELETE',
      headers: this.headers(false), // no body => no content-type (see headers())
    });
    if (res.status === 409) {
      throw new Error('Archive this client before deleting them.');
    }
    if (!res.ok) {
      throw sessionExpiryError(res) ?? new Error(`Request to ${path} failed (HTTP ${res.status})`);
    }
  }

  // --- Discrete-plan / scenario / report surface: fixture-only for now. --------
  // These back the Workspace, Scenarios and Report pages, which are hidden from
  // the live-client flow. They intentionally throw rather than fabricate data.

  getPlan(_clientId: string): Promise<ClientPlan> {
    return notLiveYet('The plan view');
  }

  upsertAsset(_clientId: string, _item: Asset): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  upsertIncomeStream(_clientId: string, _item: IncomeStream): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  upsertSpendingItem(_clientId: string, _item: SpendingItem): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  upsertLiability(_clientId: string, _item: Liability): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  upsertLifeEvent(_clientId: string, _item: LifeEvent): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  removePlanItem(_clientId: string, _kind: PlanItemKind, _itemId: string): Promise<ClientPlan> {
    return notLiveYet('Editing the plan');
  }

  getAssumptions(_clientId: string): Promise<Assumptions> {
    return notLiveYet('Assumptions');
  }

  updateAssumptions(_clientId: string, _next: Assumptions): Promise<Assumptions> {
    return notLiveYet('Assumptions');
  }

  listScenarios(_clientId: string): Promise<Scenario[]> {
    return notLiveYet('Scenarios');
  }

  saveScenario(_clientId: string, _scenario: Scenario): Promise<Scenario> {
    return notLiveYet('Scenarios');
  }

  deleteScenario(_clientId: string, _scenarioId: string): Promise<void> {
    return notLiveYet('Scenarios');
  }

  runWealthProjection(_request: WealthProjectionRequest): Promise<WealthProjectionResult> {
    return notLiveYet('This projection');
  }

  getReportData(_clientId: string, _scenarioId: string | null): Promise<ReportExport> {
    return notLiveYet('The report');
  }
}
